import React, { useMemo, useState } from 'react'
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form'
import { Badge, Card, CardBody, Input, Label, Modal } from 'reactstrap'
import Select from 'react-select'

import { HardwareDto, HardwareFeature, SelectOption } from '../hardware.type'
import { useCountries } from '../hardware.hook'
import {
  formatNiceName,
  formatNotNiceName,
  formatValueBasedOnKey,
  getCountriesWithExistingDefaultTerminal,
  getDifferencesForUpdatedHardware,
  getNiceNameOfField,
} from '../hardware.utils'
import { createHardware, updateHardware } from '../hardware.api'
import CountryAPIResponse from 'src/api-dtos/country/countries-response.dto.interface'

const DEFAULT_HARDWARE: Partial<HardwareDto> = {
  name: '',
  stripeProductId: '',
  mintsoftStockLevel: 0,
  countriesAvailableIn: [],
  fallbackToStripe: true,
  isTerminal: false,
  isDefaultTerminalForCountry: false,
  mintsoftSku: '',
  stripeSku: '',
  hardwareParentId: null,
}

// --- Internal: form fields ---

type FieldsProps = {
  allHardware: HardwareDto[]
  countriesOptions: SelectOption[]
  overwriteDefaultTerminals: boolean
  setOverwriteDefaultTerminals: (v: boolean) => void
  currentHardwareId?: string
}

const HardwareFields: React.FC<FieldsProps> = ({
  allHardware,
  countriesOptions,
  overwriteDefaultTerminals,
  setOverwriteDefaultTerminals,
  currentHardwareId,
}) => {
  const { control, register, watch, setValue, getValues } =
    useFormContext<HardwareDto>()
  const [isAccessory, setIsAccessory] = useState(
    !!getValues('hardwareParentId'),
  )

  const isTerminal = watch('isTerminal')
  const isDefaultTerminalForCountry = watch('isDefaultTerminalForCountry')
  const fallbackToStripe = watch('fallbackToStripe')
  const hardwareParentId = watch('hardwareParentId')
  const name = watch('name')

  const parentOption = useMemo<SelectOption | null>(() => {
    const parent = allHardware.find((h) => h.id === hardwareParentId)
    return parent?.id
      ? { label: formatNiceName(parent.name), value: parent.id }
      : null
  }, [allHardware, hardwareParentId])

  const countriesWithExistingDefaultTerminal =
    getCountriesWithExistingDefaultTerminal(
      allHardware,
      watch('countriesAvailableIn') ?? [],
      countriesOptions,
    )
  const availableHardwareFeatures: HardwareFeature[] = [
    'dock',
    'wifi',
    'cellular',
  ]
  const hardwareFeatureOptions: { label: string; value: HardwareFeature }[] =
    availableHardwareFeatures.map((feature) => ({
      label: formatNiceName(feature),
      value: feature,
    }))

  return (
    <>
      <div className="mb-3">
        <Label htmlFor="hardware-name" className="form-Label">
          Name{' '}
          {name ? <span className="card-title-desc mb-2">({name})</span> : ''}
        </Label>
        <Input
          className="form-control"
          type="text"
          placeholder="Name of Hardware"
          defaultValue={formatNiceName(getValues('name') ?? '')}
          onChange={(e) =>
            setValue('name', formatNotNiceName(e.target.value), {
              shouldDirty: true,
            })
          }
          id="hardware-name"
        />
      </div>

      <div className="mb-3">
        <Label htmlFor="hardware-stripe-product-id" className="form-Label">
          Stripe Product ID
        </Label>
        <input
          className="form-control"
          type="text"
          placeholder="prod_abcXYZ123"
          {...register('stripeProductId')}
          id="hardware-stripe-product-id"
        />
      </div>

      <div className="mb-3">
        <Label htmlFor="hardware-mintsoft-stock-level" className="form-Label">
          Mintsoft Stock Level
        </Label>
        <input
          className="form-control"
          type="number"
          step="1"
          placeholder="0"
          {...register('mintsoftStockLevel', { valueAsNumber: true })}
          id="hardware-mintsoft-stock-level"
        />
      </div>

      <div className="mb-3">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="is-terminal"
            {...register('isTerminal')}
          />
          <label className="form-check-label" htmlFor="is-terminal">
            Is a Terminal
          </label>
        </div>
      </div>

      {isTerminal && (
        <div className="mb-3">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="is-default-terminal"
              {...register('isDefaultTerminalForCountry')}
            />
            <label className="form-check-label" htmlFor="is-default-terminal">
              Is Default Terminal for country/countries
            </label>
            {isDefaultTerminalForCountry &&
              countriesWithExistingDefaultTerminal.length > 0 && (
                <>
                  <br />
                  <strong className="mb-0 text-danger">
                    The following countries already have default terminals:
                  </strong>
                  {countriesWithExistingDefaultTerminal.map((country) => (
                    <p key={country} className="mb-0 text-danger">
                      {country}
                    </p>
                  ))}
                  <p className="mb-0 text-danger">
                    (This can be ignored if this is already the default terminal
                    for the chosen countries)
                  </p>
                </>
              )}
          </div>
        </div>
      )}

      {isTerminal &&
        isDefaultTerminalForCountry &&
        countriesWithExistingDefaultTerminal.length > 0 && (
          <div className="mb-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="overwrite-default-terminal"
                checked={overwriteDefaultTerminals}
                onChange={() =>
                  setOverwriteDefaultTerminals(!overwriteDefaultTerminals)
                }
              />
              <label
                className="form-check-label text-danger"
                htmlFor="overwrite-default-terminal"
              >
                Overwrite{' '}
                <strong>{name ? formatNiceName(name) : 'this terminal'}</strong>{' '}
                as the new default for the above countries
              </label>
            </div>
          </div>
        )}

      {allHardware.length > 0 && (
        <div className="mb-3">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="is-accessory"
              checked={isAccessory}
              onChange={() => {
                const next = !isAccessory
                setIsAccessory(next)
                if (!next) setValue('hardwareParentId', null)
              }}
            />
            <label className="form-check-label" htmlFor="is-accessory">
              Is an accessory to another hardware item
            </label>
          </div>
        </div>
      )}

      {allHardware.length > 0 && isAccessory && (
        <div className="mb-3">
          <Label htmlFor="parent-hardware" className="form-label">
            Parent Hardware
          </Label>
          <Select
            value={parentOption}
            onChange={(selection: SelectOption) =>
              setValue('hardwareParentId', selection.value)
            }
            options={allHardware
              .filter(
                (h): h is HardwareDto & { id: string } =>
                  !!h.id && h.id !== currentHardwareId,
              )
              .map((h) => ({ label: formatNiceName(h.name), value: h.id }))}
            classNamePrefix="select2-selection"
            inputId="parent-hardware"
          />
        </div>
      )}

      <div className="mb-3">
        <Label htmlFor="countries-available-in" className="form-label">
          Countries Available In
        </Label>
        <Controller
          control={control}
          name="countriesAvailableIn"
          render={({ field }) => (
            <Select
              inputId="countries-available-in"
              value={(field.value ?? []).map(
                (code) =>
                  countriesOptions.find((co) => co.value === code) ?? {
                    label: code,
                    value: code,
                  },
              )}
              onChange={(list: SelectOption[]) =>
                field.onChange((list ?? []).map((i) => i.value))
              }
              isMulti
              options={countriesOptions}
              className="basic-multi-select"
              classNamePrefix="select"
            />
          )}
        />
      </div>

      <div className="mb-3">
        <Label htmlFor="hardware-features" className="form-label">
          Hardware Features
        </Label>
        <Controller
          control={control}
          name="hardwareFeatures"
          render={({ field }) => (
            <Select
              inputId="hardware-features"
              value={(field.value ?? []).map(
                (feature) =>
                  hardwareFeatureOptions.find((o) => o.value === feature) ?? {
                    label: feature,
                    value: feature,
                  },
              )}
              onChange={(list: { label: string; value: HardwareFeature }[]) =>
                field.onChange((list ?? []).map((i) => i.value))
              }
              isMulti
              options={hardwareFeatureOptions}
              className="basic-multi-select"
              classNamePrefix="select"
            />
          )}
        />
      </div>

      <hr />

      <div className="mb-3">
        <div className="form-check">
          <Controller
            control={control}
            name="fallbackToStripe"
            render={({ field }) => (
              <input
                className="form-check-input"
                type="checkbox"
                id="fallback-to-stripe"
                checked={!field.value}
                onChange={(e) => field.onChange(!e.target.checked)}
              />
            )}
          />
          <label className="form-check-label" htmlFor="fallback-to-stripe">
            Provided by Mintsoft Only
          </label>
        </div>
      </div>

      {fallbackToStripe && (
        <div className="mb-3">
          <Label htmlFor="hardware-stripe-sku" className="form-Label">
            Stripe SKU
          </Label>
          <input
            className="form-control"
            type="text"
            placeholder="thsku_abcXYZ123"
            {...register('stripeSku')}
            id="hardware-stripe-sku"
          />
        </div>
      )}

      <div className="mb-3">
        <Label htmlFor="hardware-mintsoft-sku" className="form-Label">
          Mintsoft SKU
        </Label>
        <input
          className="form-control"
          type="text"
          placeholder="ABC-123"
          {...register('mintsoftSku')}
          id="hardware-mintsoft-sku"
        />
      </div>
    </>
  )
}

// --- Internal: confirm diff view ---

type ConfirmDiffProps = {
  differences: Record<string, { oldValue: any; newValue: any }>
  allHardware: HardwareDto[]
  countriesData: CountryAPIResponse[]
}

const ConfirmDiff: React.FC<ConfirmDiffProps> = ({
  differences,
  allHardware,
  countriesData,
}) => (
  <div className="mb-4">
    <h5 className="font-size-14 mb-1">Proposed Changes:</h5>
    {Object.entries(differences).map(([key, { oldValue, newValue }]) => (
      <div key={key} className="m-3">
        <Card>
          <CardBody>
            <h5 className="card-title">{getNiceNameOfField(key)}</h5>
            <br />
            <p className="card-text">
              <strong className="bg-soft-danger">
                <s>
                  {formatValueBasedOnKey(
                    allHardware,
                    countriesData,
                    key,
                    oldValue,
                  )}
                </s>
              </strong>
              <br />
              <strong className="bg-soft-success">
                {formatValueBasedOnKey(
                  allHardware,
                  countriesData,
                  key,
                  newValue,
                )}
              </strong>
            </p>
          </CardBody>
        </Card>
      </div>
    ))}
  </div>
)

// --- Exported: main modal ---

type HardwareFormModalProps = {
  hardware?: HardwareDto
  allHardware: HardwareDto[]
  onClose: () => void
  onSuccess: () => void
}

const HardwareFormModal: React.FC<HardwareFormModalProps> = ({
  hardware,
  allHardware,
  onClose,
  onSuccess,
}) => {
  const isCreate = !hardware
  const methods = useForm<HardwareDto>({
    defaultValues: hardware ?? DEFAULT_HARDWARE,
  })
  const [step, setStep] = useState<'form' | 'confirm'>('form')
  const [overwriteDefaultTerminals, setOverwriteDefaultTerminals] =
    useState(false)
  const [pendingDiff, setPendingDiff] = useState<Record<
    string,
    { oldValue: any; newValue: any }
  > | null>(null)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [copyIdText, setCopyIdText] = useState('Copy')

  const { data: countriesRaw = [], isLoading: countriesLoading } =
    useCountries()
  const countriesOptions = useMemo<SelectOption[]>(
    () =>
      countriesRaw.map((c) => ({
        label: `${c.flag} ${c.name}`,
        value: c.code,
      })),
    [countriesRaw],
  )

  const watchedCountries = methods.watch('countriesAvailableIn') ?? []
  const watchedIsDefault = methods.watch('isDefaultTerminalForCountry')
  const conflictingCountries = getCountriesWithExistingDefaultTerminal(
    allHardware,
    watchedCountries,
    countriesOptions,
  )
  const submitDisabled =
    watchedIsDefault &&
    conflictingCountries.length > 0 &&
    !overwriteDefaultTerminals

  const copyHardwareId = (id: string) => {
    navigator.clipboard.writeText(id).then(
      () => setCopyIdText('Copied 🎉'),
      () => setCopyIdText('Could not copy ID 😭'),
    )
    setTimeout(() => setCopyIdText('Copy'), 2500)
  }

  const onFormSubmit = async (values: HardwareDto) => {
    if (isCreate) {
      setError('')
      setSubmitting(true)
      try {
        await createHardware(values, overwriteDefaultTerminals)
        onSuccess()
      } catch (e: any) {
        setError(e.message)
      } finally {
        setSubmitting(false)
      }
    } else {
      const diff = getDifferencesForUpdatedHardware(hardware, values)
      if (!Object.keys(diff).length) {
        onClose()
        return
      }
      setPendingDiff(diff)
      setStep('confirm')
    }
  }

  const onConfirmUpdate = async () => {
    if (!hardware?.id || !pendingDiff) return
    setError('')
    setSubmitting(true)

    const updateBody: Partial<HardwareDto> = {}
    for (const key in pendingDiff) {
      updateBody[key as keyof HardwareDto] = pendingDiff[key].newValue
    }

    try {
      await updateHardware(hardware.id, updateBody, overwriteDefaultTerminals)
      onSuccess()
    } catch (e: any) {
      setError(e.message)
      setStep('form')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal size="lg" isOpen toggle={onClose} scrollable>
      <div className="modal-header">
        <h5 className="modal-title mt-0">
          {step === 'confirm'
            ? 'Confirm Changes'
            : isCreate
              ? 'New Hardware'
              : formatNiceName(hardware.name)}
          {!isCreate && hardware.id && (
            <>
              <br />
              <p className="card-title-desc mb-2">
                {hardware.name}{' '}
                <button
                  type="button"
                  className="btn btn-link p-0 align-baseline"
                  onClick={() => copyHardwareId(hardware.id!)}
                >
                  [{copyIdText}]
                </button>
              </p>
            </>
          )}
        </h5>
        <button
          type="button"
          onClick={onClose}
          className="close"
          data-dismiss="modal"
          aria-label="Close"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>

      <div className="modal-body">
        {countriesLoading ? (
          <div>Loading...</div>
        ) : step === 'form' ? (
          <FormProvider {...methods}>
            <HardwareFields
              allHardware={allHardware}
              countriesOptions={countriesOptions}
              overwriteDefaultTerminals={overwriteDefaultTerminals}
              setOverwriteDefaultTerminals={setOverwriteDefaultTerminals}
              currentHardwareId={hardware?.id}
            />
          </FormProvider>
        ) : (
          pendingDiff && (
            <ConfirmDiff
              differences={pendingDiff}
              allHardware={allHardware}
              countriesData={countriesRaw}
            />
          )
        )}
      </div>

      <div className="modal-footer">
        {error && <Badge className="me-2 bg-danger">{error}</Badge>}
        {step === 'confirm' && (
          <button
            type="button"
            className="btn btn-secondary me-2"
            onClick={() => setStep('form')}
            disabled={submitting}
          >
            Back
          </button>
        )}
        <button
          type="button"
          className="btn btn-primary"
          disabled={submitting || (step === 'form' && !!submitDisabled)}
          onClick={
            step === 'form'
              ? methods.handleSubmit(onFormSubmit)
              : onConfirmUpdate
          }
        >
          {submitting
            ? 'Saving...'
            : step === 'confirm'
              ? 'Yes, Save Changes'
              : isCreate
                ? 'Create Hardware'
                : 'Save Changes'}
        </button>
      </div>
    </Modal>
  )
}

export default HardwareFormModal
